/*
 * House of Daisy — collection filters (snippets/hod-facets.liquid).
 *
 * Phone: each filter group is a sheet from the bottom. Changes inside a
 * sheet are counted before she commits ("Show 9 pieces"), by asking the
 * grid section to render with the new filters and reading its count.
 * Sort and "When do you need it" apply at once. Desktop: the groups sit
 * open in a rail and every change applies at once.
 *
 * Applying is a plain navigation to the filtered URL, so the back button,
 * sharing a link and reloading all behave.
 */
(() => {
  if (customElements.get('hod-facets')) return;

  const desktop = window.matchMedia('(min-width: 990px)');

  class HodFacets extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.backdrop = this.querySelector('.hod-facets__backdrop');
      this.openSheet = null;
      this.countRequest = 0;

      this.addEventListener('click', (event) => this.onClick(event));
      this.form.addEventListener('change', (event) => this.onChange(event));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.openSheet) this.close();
      });
    }

    onClick(event) {
      const opener = event.target.closest('[data-hod-open]');
      if (opener) {
        this.open(document.getElementById(opener.dataset.hodOpen), opener);
        return;
      }
      if (event.target.closest('[data-hod-close]')) {
        this.close();
        return;
      }
      const clear = event.target.closest('[data-hod-clear]');
      if (clear) {
        const sheet = clear.closest('[data-hod-sheet]');
        sheet.querySelectorAll('input[type="checkbox"]').forEach((input) => (input.checked = false));
        const anyPrice = sheet.querySelector('[data-hod-band][value="0-0"]');
        if (anyPrice) {
          anyPrice.checked = true;
          this.setBand(anyPrice.value);
        }
        this.updateCount();
      }
    }

    onChange(event) {
      const input = event.target;
      if (input.matches('[data-hod-band]')) this.setBand(input.value);
      if (desktop.matches || input.matches('[data-hod-now]')) {
        this.apply();
        return;
      }
      this.updateCount();
    }

    /* A price band sets the two hidden bounds; "0" means open-ended. */
    setBand(value) {
      const [low, high] = value.split('-');
      const gte = this.querySelector('[data-hod-gte]');
      const lte = this.querySelector('[data-hod-lte]');
      if (gte) gte.value = low === '0' ? '' : low;
      if (lte) lte.value = high === '0' ? '' : high;
    }

    params() {
      const params = new URLSearchParams();
      for (const [name, value] of new FormData(this.form)) {
        if (name === 'hod_price' || value === '') continue;
        params.append(name, value);
      }
      return params;
    }

    apply() {
      const query = this.params().toString();
      window.location.assign(`${this.dataset.url}${query ? `?${query}` : ''}`);
    }

    open(sheet, opener) {
      if (!sheet) return;
      this.close();
      this.openSheet = sheet;
      this.opener = opener;
      sheet.classList.add('is-open');
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
      opener.setAttribute('aria-expanded', 'true');
      this.backdrop.hidden = false;
      document.body.classList.add('hod-sheet-open');
      const first = sheet.querySelector('input:not([disabled]), button');
      if (first) first.focus();
      this.updateCount();
    }

    close() {
      if (!this.openSheet) return;
      this.openSheet.classList.remove('is-open');
      this.openSheet.removeAttribute('role');
      this.openSheet.removeAttribute('aria-modal');
      this.backdrop.hidden = true;
      document.body.classList.remove('hod-sheet-open');
      if (this.opener) {
        this.opener.setAttribute('aria-expanded', 'false');
        this.opener.focus();
      }
      this.openSheet = null;
    }

    /* Ask the grid section how many pieces the pending filters leave. */
    async updateCount() {
      if (!this.openSheet || this.openSheet.classList.contains('hod-sheet--sort')) return;
      const request = ++this.countRequest;
      const params = this.params();
      params.set('section_id', this.dataset.section);
      try {
        const response = await fetch(`${this.dataset.url}?${params.toString()}`);
        const html = await response.text();
        if (request !== this.countRequest) return;
        const match = html.match(/data-hod-count="(\d+)"/);
        if (!match) return;
        const count = Number(match[1]);
        this.querySelectorAll('[data-hod-show]').forEach((button) => {
          if (count === 0) {
            button.textContent = this.dataset.showNone;
            button.disabled = true;
          } else {
            button.textContent =
              count === 1 ? this.dataset.showOne : this.dataset.showMany.replace('9999', count.toLocaleString('en-IN'));
            button.disabled = false;
          }
        });
      } catch (error) {
        /* Offline or blocked: the button keeps its last count and still applies. */
      }
    }
  }

  customElements.define('hod-facets', HodFacets);
})();
